import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api } from "../../api/client";
import { qk, useEnvTemplates } from "../../api/queries";
import type { Task } from "../../api/types";
import { useUploads } from "../../lib/useUploads";
import { UploadList } from "../Attachments";
import { Icon } from "../Icon";
import { MarkdownEditor } from "../MarkdownEditor";
import { Modal } from "../Modal";

export function CreateTaskModal({
  pid,
  onClose,
  onCreated,
}: {
  pid: string;
  onClose: () => void;
  onCreated: (t: Task) => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [criteria, setCriteria] = useState("");
  const { data: templates } = useEnvTemplates(pid);
  const [templateId, setTemplateId] = useState<string>("");
  const uploads = useUploads();
  const fileInput = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const create = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.createTask(pid, {
        title: title.trim(),
        description,
        acceptance_criteria: criteria,
        ...(templateId ? { env_template_id: templateId } : {}),
        ...(uploads.ids.length ? { attachment_ids: uploads.ids } : {}),
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: qk.tasks(pid) });
      onCreated(t);
    },
  });

  const defaultTemplate = templates?.find((t) => t.is_default);

  return (
    <Modal
      title="New task"
      onClose={onClose}
      width={720}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!title.trim() || create.isPending || uploads.busy}
            onClick={() => create.mutate()}
          >
            {create.isPending ? "Creating…" : "Create task"}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <input
          className="input input-title"
          placeholder="Untitled"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Title"
        />
        <div className="field">
          <span className="field-label">Description</span>
          <MarkdownEditor
            value={description}
            onChange={setDescription}
            rows={7}
            placeholder="What needs to be done, and why. Paste or drop screenshots and files."
            onFiles={uploads.addFiles}
            footer={<UploadList items={uploads.items} onRemove={uploads.remove} />}
          />
          <div>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileInput.current?.click()}>
              <Icon name="paperclip" size={14} /> Attach files
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                if (e.target.files) uploads.addFiles(e.target.files);
                e.target.value = "";
              }}
            />
          </div>
        </div>
        <div className="field">
          <span className="field-label">Acceptance criteria</span>
          <MarkdownEditor
            value={criteria}
            onChange={setCriteria}
            rows={4}
            placeholder={"- [ ] …"}
          />
        </div>
        <label className="field">
          <span className="field-label">Environment template</span>
          <select className="input" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Project default{defaultTemplate ? ` (${defaultTemplate.name})` : ""}</option>
            {templates?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} · {t.os} · {t.backend}
              </option>
            ))}
          </select>
        </label>
        {create.error && <div className="error-box">{create.error.message}</div>}
      </div>
    </Modal>
  );
}
